import { useEffect, useRef, useState } from "react";

import { Flex, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { Page } from "shared/components/Page";
import { ScannableInput, ScannableInputHandle } from "shared/components/ScannableInput";
import { IntlMessageId } from "shared/lang";
import { Button } from "ui/Button/Button";
import { Form } from "ui/Form/Form";
import { AddIcon } from "ui/Icons/Icons";
import { RemovableListItem } from "ui/RemovableListItem/RemovableListItem";
import { BodyM, HeaderS } from "ui/Typography/Typography";

import { EndPickingButton } from "../../components/EndPickingButton";
import { OrderCompletionWarningModal } from "../../components/OrderCompletionWarningModal";
import { PickingConflictModal } from "../../components/PickingConflictModal";
import { ShelfAssignmentSkipConfirmationModal } from "../../components/ShelfAssignmentSkipConfirmationModal";
import { SkipScanningButton } from "../../components/SkipScanningButton";
import { usePickingActions } from "../../hooks/usePickingActions";
import { usePickingUIStore } from "../../hooks/usePickingUIStore";
import { usePickingStore } from "../../stores/usePickingStore";

const isValidShelfId = (shelfId: string): boolean => {
  return /^[0-9]{1,3}$/.test(shelfId);
};

export function AssignShelvesPage() {
  const intl = useIntl();

  const { endPicking, retryEndPicking, confirmOrderCompletion, resetPicking } = usePickingActions();
  const store = usePickingStore;
  const shelvesIds = usePickingStore((state) => state.shelvesIds);
  const endPickingLoading = usePickingStore((state) => state.endPickingPhase === "loading");
  const endPickingError = usePickingStore((state) => state.endPickingPhase === "error");

  const [errorTextId, setErrorTextId] = useState<IntlMessageId | undefined>(undefined);
  const [typedValue, setTypedValue] = useState<string>();

  const refInput = useRef<ScannableInputHandle | null>(null);
  const [isAddButtonVisible, setIsAddButtonVisible] = useState(false);
  const { shouldShowOrderCompletionWarning, endPickingConflictData } = usePickingUIStore(
    useShallow((state) => ({
      shouldShowOrderCompletionWarning: state.shouldShowOrderCompletionWarning,
      endPickingConflictData: state.endPickingConflictData,
    })),
  );

  const handleValueChange = (value: string) => {
    const isValid = isValidShelfId(value);
    setIsAddButtonVisible(value !== "");
    setErrorTextId(
      isValid || !value
        ? undefined
        : ("pages.picking.picking.assign-shelves.input-error-invalid-format" as IntlMessageId),
    );
  };

  const scanShelf = (newShelfId: string) => {
    if (!isValidShelfId(newShelfId)) {
      setErrorTextId(
        "pages.picking.picking.assign-shelves.input-error-invalid-format" as IntlMessageId,
      );
      return;
    }

    if (newShelfId === "") {
      return;
    }

    if (shelvesIds.find((id) => id === newShelfId)) {
      setErrorTextId("pages.picking.assign-shelves.input-error-already-scanned");
      return;
    }

    store.getState().addShelf(newShelfId);
    setIsAddButtonVisible(false);
    refInput?.current?.resetValue();
  };

  const onFormSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (errorTextId) {
      return;
    }
    scanShelf(typedValue ?? "");
    setTypedValue(undefined);
    if (endPickingError) {
      retryEndPicking();
    } else {
      endPicking();
    }
  };

  const onComfirmOrderCompletionWarning = () => {
    confirmOrderCompletion();
  };

  const {
    isOpen: isShelfAssignmentSkipConfirmationModalOpen,
    onOpen: openShelfAssignmentSkipConfirmationModal,
    onClose: closeShelfAssignmentSkipConfirmationModal,
  } = useDisclosure();

  const onClickSkipButton = () => {
    openShelfAssignmentSkipConfirmationModal();
  };

  const onConfirmSkip = () => {
    store.getState().resetShelves();
    if (endPickingError) {
      retryEndPicking();
    } else {
      endPicking();
    }
    closeShelfAssignmentSkipConfirmationModal();
  };

  useEffect(() => {
    usePickingUIStore.getState().reset();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page isCentered scrollBehavior="auto">
      <HeaderS textAlign="center">
        <FormattedMessage id="pages.picking.assign-shelves.title" />
      </HeaderS>
      <Form onSubmit={onFormSubmit} alignItems="center" w="100%">
        {shelvesIds.map((shelfId) => (
          <RemovableListItem
            key={shelfId}
            title={shelfId}
            onClickRemove={() => {
              setTypedValue(undefined);
              store.getState().removeShelf(shelfId);
            }}
          />
        ))}
        <ScannableInput
          ref={refInput}
          inputName="container-id"
          inputMode="numeric"
          error={errorTextId ? intl.formatMessage({ id: errorTextId }) : undefined}
          placeholder={intl.formatMessage({
            id: "pages.picking.assign-shelves.input-placeholder",
          })}
          onKeyboardChange={(value) => {
            store.getState().setInputMethod("MANUAL");
            setTypedValue(value);
          }}
          onScan={(scannedValue) => {
            store.getState().setInputMethod("SCAN");
            setTypedValue(undefined);
            scanShelf(scannedValue);
          }}
          isAutoFocus
          showKeyBoard
          onValueChange={handleValueChange}
        />
        {isAddButtonVisible && (
          <Flex pt="s100" justify="flex-end">
            <Button
              variant="secondary"
              aria-label="Add shelf"
              alignSelf="flex-end"
              data-testid="add-shelf-button"
              onClick={() => {
                if (refInput.current?.value) {
                  scanShelf(refInput.current.value);
                }
              }}
            >
              <AddIcon mr="s100" color="pinkFlink.500" />
              <BodyM color="pinkFlink.500">
                {intl.formatMessage({
                  id: "pages.picking.assign-shelves.add-shelf-button",
                })}
              </BodyM>
            </Button>
          </Flex>
        )}
        <EndPickingButton
          beforeSubmit={() => {
            if (typedValue && !shelvesIds.find((s) => s === typedValue)) {
              store.getState().addShelf(typedValue);
              setTypedValue(undefined);
              refInput?.current?.resetValue();
            }
          }}
          isButtonDisabled={(!shelvesIds.length && !typedValue) || !!errorTextId}
          mt="s300"
        />
      </Form>
      <SkipScanningButton onClickSkipButton={onClickSkipButton} isDisabled={endPickingLoading} />
      {shouldShowOrderCompletionWarning && (
        <OrderCompletionWarningModal onClick={onComfirmOrderCompletionWarning} />
      )}
      {isShelfAssignmentSkipConfirmationModalOpen && (
        <ShelfAssignmentSkipConfirmationModal
          onClickYes={onConfirmSkip}
          onClickNo={closeShelfAssignmentSkipConfirmationModal}
        />
      )}
      {endPickingConflictData && (
        <PickingConflictModal
          isOpen
          hideProceed
          conflictData={endPickingConflictData}
          onClose={() => {
            resetPicking();
          }}
          onProceed={() => {
            resetPicking();
          }}
        />
      )}
    </Page>
  );
}
